import React, {Fragment} from 'react';

import {Stack, Typography} from '@mui/material';
import {
	AppointmentBookingEnum,
	FinishWithoutCompleteFlowEnum,
	OdontoBookingTaggingEnum,
} from 'main/analytics/gaTagsEnum';
import {AlertModal} from 'presentation/components';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {BookingNotFoundVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundDialog/types';
import {FlowEnum} from 'presentation/pages/private/booking/constants';
import {CheckBookingModalEnum} from 'presentation/pages/private/booking/hooks/useBookingSpecialty/types';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {ExamCard} from '../../../../components/ExamCard';
import {useExamSelection} from './hooks/useExamSelection';
import {StyledTypography} from './styles';
import {ExamSelectionProps} from './types';
import {ExamOrientationsDialog} from './components/ExamOrientationsDialog';
import {ExamNotFound} from './components/ExamNotFound';
import {BookingErrorState} from 'presentation/modules/booking/shared/ui/components/BookingErrorState';
import {BookingEmptyState} from 'presentation/modules/booking/shared/ui/components/BookingEmptyState';
import {BookingLoadingState} from 'presentation/modules/booking/shared/ui/components/BookingLoadingState';
import {BookingNotFoundListCard} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundListCard';
import {PendingDialog} from 'presentation/modules/booking/shared/ui/components/PendingDialog';
import {ExamDuplicityDialog} from './components/ExamDuplicityDialog';
import Grid from '@mui/material/Grid2';
import {ExamAuthRequiredDrawer} from './components/ExamAuthRequiredDrawer';
import {ExamEmptyState} from 'presentation/modules/booking/shared/ui/components/ExamEmptyState';
import {OnDemandExamDrawer} from 'presentation/modules/booking/shared/ui/components/OnDemandExamDrawer';

const {bookExams} = translations['pt-br'].privateFlow.services;
const {loadingExamsError, loadingExamsState} =
	translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

const {notFoundModal} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

const {serverError, serverErrorTitle, tryAgain, unexpectedError, emptyDescription, resultFound} =
	translations['pt-br'].commons;

const {emptyStateMedicalGuide} = translations['pt-br'].privateFlow.bookingFlow.examSelection;

export function ExamSelection({handleNextStep}: ExamSelectionProps) {
	const {
		loadingText,
		isFetching,
		showNotFoundState,
		handleNavToStateSelection,
		LIST,
		showEmptyState,
		handleSelectCardExam,
		checkBookingModal,
		scheduledBooking,
		resetBookingDataSpecialties,
		handleAgreeExamOrientations,
		examOrientationsModal,
		setExamOrientationsModal,
		refetch,
		examsListError,
		examListHasUnprocessableError,
		setCheckBookingModal,
		isSelectedExamAuthRequired,
		handleNavigateToExamAuth,
		examSelected,
		handleCloseExamAuthRequiredDrawer,
		query,
		isExamsSpecialtyProcedureFlagEnabled,
		isSemanticSearchEnabled,
		clearExamSelection,
		bookingData,
		isOnDemandExamsEnabled,
		isOnDemandExamDrawerOpen,
		hasExamPreparation,
		handleOpenOnDemandExamDrawer,
		handleCloseOnDemandExamDrawer,
		handleViewOnDemandExamUnits,
	} = useExamSelection({handleNextStep});

	const {isEnabled: isSchedulingByMedicalGuideEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULING_BY_MEDICAL_GUIDE,
	);

	if (isFetching) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={bookExams}
				loadingText={loadingExamsState}
			/>
		);
	}

	if (examsListError && !examListHasUnprocessableError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={bookExams}
				errorTitle={loadingExamsError}
				handleRefresh={refetch}
			/>
		);
	}

	if (showNotFoundState) {
		return isExamsSpecialtyProcedureFlagEnabled && isSemanticSearchEnabled ? (
			<ExamEmptyState
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA}
			/>
		) : (
			<ExamNotFound handleAction={handleNavToStateSelection} />
		);
	}

	if (showEmptyState) {
		const options =
			isSchedulingByMedicalGuideEnabled && bookingData?.isBookingByMedicalGuideFlow
				? {
						title: emptyStateMedicalGuide.title,
						description: emptyStateMedicalGuide.description,
						primaryButtonText: emptyStateMedicalGuide.primaryButtonText,
						primaryButtonAction: clearExamSelection,
					}
				: undefined;

		return isExamsSpecialtyProcedureFlagEnabled && isSemanticSearchEnabled ? (
			<ExamEmptyState
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA}
				options={options}
			/>
		) : (
			<BookingEmptyState
				description={emptyDescription}
				flowType={FlowEnum.EXAM}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_ESPECIALIDADE_ENCONTRADA}
			/>
		);
	}

	return (
		<Fragment>
			{query ? (
				<Typography variant="subtitle2" color="text.secondary">
					{resultFound}
				</Typography>
			) : null}

			{LIST?.map(categoryItem =>
				categoryItem?.list?.length === 0 ? null : (
					<Stack key={categoryItem?.category} mt={2} gap={2}>
						<StyledTypography variant="h6" id={categoryItem?.category}>
							{categoryItem?.category}
						</StyledTypography>
						<Grid container rowSpacing={2} columnSpacing={3}>
							{categoryItem?.list.map((item, index) => (
								<Grid key={index} size={{xs: 12, lg: 6}}>
									<ExamCard
										dataTagId={AppointmentBookingEnum.SELECIONOU_EXAME}
										text={formatText(item?.formattedText)}
										handleClick={() => handleSelectCardExam(item)}
										isOnDemandExam={
											isOnDemandExamsEnabled && item?.flLivreDemanda
										}
									/>
								</Grid>
							))}
						</Grid>
					</Stack>
				),
			)}

			<Stack mt={2}>
				<BookingNotFoundListCard
					openModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.EXAM}`}
					modalHeaderModalDataTagId={`${OdontoBookingTaggingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.EXAM}`}
					modalCloseModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.EXAM}`}
					modalFirstActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO}${FlowEnum.EXAM}`}
					modalSecondActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_ONLINE}${FlowEnum.EXAM}`}
					flowType={FlowEnum.EXAM}
					customOrientationText={notFoundModal.examCardListText}
					variant={BookingNotFoundVariantEnum.SPECIALTY}
				/>
			</Stack>

			{scheduledBooking ? (
				<ExamDuplicityDialog
					open={checkBookingModal === CheckBookingModalEnum.DUPLICITY_STATE}
					setOpen={() => {
						resetBookingDataSpecialties();
						setCheckBookingModal('none');
					}}
					scheduledBooking={scheduledBooking}
				/>
			) : null}

			<PendingDialog
				open={
					checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ||
					checkBookingModal === CheckBookingModalEnum.VERIFYING_DUPLICITY
				}
				text={loadingText}
			/>

			<OnDemandExamDrawer
				open={isOnDemandExamDrawerOpen}
				specialtyName={formatText(examSelected)}
				hasExamPreparation={hasExamPreparation}
				onOpen={handleOpenOnDemandExamDrawer}
				onClose={handleCloseOnDemandExamDrawer}
				onViewUnits={handleViewOnDemandExamUnits}
			/>

			<ExamOrientationsDialog
				open={examOrientationsModal && checkBookingModal === CheckBookingModalEnum.NONE}
				setOpen={setExamOrientationsModal}
				handleAgree={handleAgreeExamOrientations}
			/>

			<ExamAuthRequiredDrawer
				examSelected={examSelected}
				handleNavigateToExamAuth={handleNavigateToExamAuth}
				handleClose={handleCloseExamAuthRequiredDrawer}
				open={isSelectedExamAuthRequired}
			/>

			<AlertModal
				open={checkBookingModal === CheckBookingModalEnum.ERROR}
				onClose={() => {
					resetBookingDataSpecialties();
					setCheckBookingModal('none');
				}}
				description={serverError}
				title={serverErrorTitle}
				headerTitle={unexpectedError}
				buttonLabel={tryAgain}
				variant="info"
			/>
		</Fragment>
	);
}
